import {useMutation, useQueryClient} from "@tanstack/react-query";
import {createFaqApi, deleteFaqApi, updateFaqApi,} from "../../../../api/support/faq";
import {ChangeEvent, useEffect, useState} from "react";
import {useReadFaqDetail} from "./useReadFaqDetail";
import {useNavigate, useParams} from "react-router-dom";
import {DeleteFaqApiProps, UpdateFaqApiProps} from "../../../../types/support/faq";
import {useModal} from "../../../../hooks";
import useFileHandler from "../../../../hooks/useFileHandler";
import {useUser} from "../../../../util/userContext/UserContext";

export const DefaultInputData = {
    faqId: "",
    title: "",
    topicTypeIdx: 0,
    productVendorIdx: 0,
    productCategoryIdx: 0,
    isNotice: "N",
    newProductGroupid: "",
    body: "",
    fileKeyList: [], // 저장/수정할 때 사용
    fileList: [], // 저장/수정할 때 사용
    fileVOList: [], // 조회결과로 사용
};

/**
 * FAQ CRUD hook
 * @returns
 */
export const useEditFaq = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const {user} = useUser();
    const {handleModal, resetModal} = useModal();
    const {id} = useParams();
    const {data} = useReadFaqDetail({id: id || ""});
    const [inputData, setInputData] = useState<any>({
        ...DefaultInputData,
        userId: user?.userId,
        userName: user?.userName,
        loginId: user?.loginId
    });
    const {removeFile, UploadUI, handleButtonClick} = useFileHandler({setter: setInputData});

    const deleteMutation = useMutation({
        mutationFn: (data: DeleteFaqApiProps) => deleteFaqApi(data),
        onSuccess: (res) => {
            if (res.code === "0000") {
                handleModal({
                    isOpen: true,
                    contents: "삭제되었습니다.",
                    type: "alert",
                    closeAction: () => queryClient.invalidateQueries({
                        queryKey: ["readFaqApi"],
                    })
                });
            }
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const updateMutation = useMutation({
        mutationFn: (data: UpdateFaqApiProps) => updateFaqApi(data),
        onSuccess: (res, variables) => {
            if (res.code === "0000") {
                handleModal({
                    isOpen: true,
                    contents: "수정되었습니다.",
                    type: "alert",
                    closeAction: () => {
                        queryClient.invalidateQueries({
                            queryKey: ["readFaqApi"],
                        });
                        queryClient.invalidateQueries({
                            queryKey: ['readFaqDetailApi', variables.faqId],
                        });
                        navigate(`${process.env.PUBLIC_URL}/support/faq/detail/${variables.faqId}`, {replace: true});
                    }
                });
            }
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const createMutation = useMutation({
        mutationFn: (data: UpdateFaqApiProps) => createFaqApi(data),
        onSuccess: (res) => {
            if (res.code === "0000") {
                handleModal({
                    isOpen: true,
                    contents: "저장되었습니다.",
                    type: "alert",
                    closeAction: () => {
                        queryClient.invalidateQueries({
                            queryKey: ["readFaqApi"],
                        });
                        navigate(`${process.env.PUBLIC_URL}/support/faq/detail/${res.result.faqId}`, {replace: true});
                    }
                });
            }
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const deleteFaq = (data: DeleteFaqApiProps) => {
        if ((data?.faqIdList?.length | 0) <= 0) {
            handleModal({
                isOpen: true,
                contents: "선택된 항목이 없습니다.",
                type: "alert",
            });
        } else {
            handleModal({
                isOpen: true,
                contents: "삭제하시겠습니까?",
                type: 'confirm',
                title: "알림",
                callback: () => {
                    deleteMutation.mutate(data);
                }
            });
        }
    };

    const saveFaq = (data: UpdateFaqApiProps) => {
        if (inputData?.newProductGroupid === "") {
            handleModal({
                isOpen: true,
                contents: "제품군을 선택해주세요.",
                type: "alert",
            });
        } else {
            if (!data.faqId) createMutation.mutate({...data, faqId: id || ""});
            else updateMutation.mutate({...data, faqId: id || ""});
        }
    };

    const onChangeInputData = (
        e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const {name, value} = e.target;
        setInputData({...inputData, [name]: value});
    };

    useEffect(() => {
        if (id)
            setInputData(data);
    }, [data]);

    return {
        deleteFaq,
        inputData,
        setInputData,
        onChangeInputData,
        saveFaq,
        removeFile,
        UploadUI,
        handleButtonClick
    };
};
